import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CheckIcon, ChevronDownIcon } from './icons'
import './Select.css'

/**
 * Uygulamanın kendi açılır listesi, sistemin (tarayıcının) listesi yerine.
 *
 * Sistem listesi her işletim sisteminde başka görünüyor ve oku kenara
 * yapışıyordu (kullanıcı, 4 Eki 2026: "hepsi uygulamanın kendi listesi
 * olsun"). Liste sayfanın en üst katmanında (portal) açılıyor: pencerelerin
 * içinde kesilmesin, bir <label>'ın içinde dururken tıklama kutuya geri
 * dönmesin. Altta yer yoksa yukarı açılıyor.
 *
 * Klavye: kutu odaktayken Enter/Boşluk/↓ açar; açıkken ↑↓ gezinir, Enter
 * seçer, Esc kapatır.
 */
export interface SelectOption {
  value: string
  label: string
  /** Sağda sönük yazı (örn. "−280 g"). */
  hint?: string
  /** Seçenek değil eylem ("+ Yeni tezgah", "Tezgahları düzenle"): renkli satır. */
  tone?: 'success' | 'warning'
  /** Yazının solunda simge (eylem satırlarında: artı, kalem). */
  icon?: ReactNode
}

interface Props {
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  disabled?: boolean
  id?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  className?: string
}

export function Select({
  value,
  options,
  onChange,
  disabled,
  id,
  className,
  ...aria
}: Props) {
  const [open, setOpen] = useState(false)
  const [hi, setHi] = useState(0)
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number; maxH: number } | null>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const list = useRef<HTMLUListElement>(null)

  const current = options.find((o) => o.value === value)

  const place = () => {
    const r = btn.current?.getBoundingClientRect()
    if (!r) return
    const gap = 6
    const below = window.innerHeight - r.bottom - gap - 8
    const above = r.top - gap - 8
    const want = Math.min(300, options.length * 46 + 12)
    // Dar bir kutunun listesi de okunsun (25 Eki 2026, telefonda tezgah adı
    // "B." diye kesiliyordu): en az 270 px, ekranın dışına taşmadan.
    const width = Math.min(Math.max(r.width, 270), window.innerWidth - 16)
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
    if (below >= Math.min(want, 180) || below >= above) {
      setPos({ left, width, top: r.bottom + gap, maxH: Math.max(120, Math.min(300, below)) })
    } else {
      setPos({ left, width, bottom: window.innerHeight - r.top + gap, maxH: Math.max(120, Math.min(300, above)) })
    }
  }

  const openList = () => {
    if (disabled) return
    setHi(Math.max(0, options.findIndex((o) => o.value === value)))
    place()
    setOpen(true)
  }

  const pick = (v: string) => {
    onChange(v)
    setOpen(false)
    btn.current?.focus()
  }

  // Kaydırma ya da boyut değişince liste kutunun yanında kalsın.
  useLayoutEffect(() => {
    if (!open) return
    const re = () => place()
    window.addEventListener('resize', re)
    window.addEventListener('scroll', re, true)
    return () => {
      window.removeEventListener('resize', re)
      window.removeEventListener('scroll', re, true)
    }
  })

  // Vurgulanan seçenek görünür kalsın.
  useEffect(() => {
    if (!open) return
    list.current?.querySelectorAll('button')[hi]?.scrollIntoView({ block: 'nearest' })
  }, [hi, open])

  // Açıkken klavye listeye ait (dışarıdaki kısayollar çalışmasın).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        btn.current?.focus()
      } else if (e.key === 'ArrowDown') setHi((h) => Math.min(options.length - 1, h + 1))
      else if (e.key === 'ArrowUp') setHi((h) => Math.max(0, h - 1))
      else if (e.key === 'Enter' || e.key === ' ') {
        const o = options[hi]
        if (o) pick(o.value)
      } else if (e.key === 'Tab') setOpen(false)
      else return
      e.preventDefault()
      e.stopPropagation()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  })

  return (
    <>
      <button
        ref={btn}
        id={id}
        type="button"
        className={`uisel ${open ? 'is-open' : ''} ${className ?? ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={(e) => {
          // <label> içindeyken tıklama iki kez sayılmasın.
          e.preventDefault()
          if (open) setOpen(false)
          else openList()
        }}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault()
            openList()
          }
        }}
        {...aria}
      >
        <span className="uisel__label">{current?.label ?? ''}</span>
        {current?.hint && <span className="uisel__hint">{current.hint}</span>}
        <ChevronDownIcon />
      </button>

      {open &&
        pos &&
        createPortal(
          <>
            <div className="uisel__scrim" onMouseDown={() => setOpen(false)} />
            <ul
              ref={list}
              className="uisel__list"
              role="listbox"
              style={{
                left: pos.left,
                width: pos.width,
                top: pos.top,
                bottom: pos.bottom,
                maxHeight: pos.maxH,
              }}
            >
              {options.map((o, i) => (
                <li key={o.value || `__${i}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={o.value === value}
                    className={`${o.value === value ? 'is-on' : ''} ${i === hi ? 'is-hi' : ''} ${o.tone ? `uisel__act uisel__act--${o.tone}` : ''}`}
                    onMouseEnter={() => setHi(i)}
                    onClick={() => pick(o.value)}
                  >
                    {o.icon && <span className="uisel__icon">{o.icon}</span>}
                    <span className="uisel__label">{o.label}</span>
                    {o.hint && <span className="uisel__hint">{o.hint}</span>}
                    <span className="uisel__tick">{o.value === value && <CheckIcon />}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>,
          document.body,
        )}
    </>
  )
}
